// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@adl/App/Components/Common/XngZone";
import HeaderClickable, { HeaderClickableStyle } from "@adl/App/Components/Header/HeaderClickable";
import MiniChangeShipTo from "@adl/App/Components/MiniChangeShipTo/MiniChangeShipTo";
import { TippyPopupStyle } from "@adl/App/Components/Tippy/Style";
import TippyPopup from "@adl/App/Components/Tippy/TippyPopup";
import useHeaderHeight from "@adl/App/Hooks/useHeaderHeight";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { CartLinkZoneName, SignInZoneName } from "@adl/XDL/constants";
import Location from "@adl/XDL/Icons/Location";
import QuickOrder from "@adl/XDL/Icons/QuickOrder";
import { color, xGetFulfillmentLabel, xIsAuthenticated, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FindLocationModal from "@insite/content-library/Components/FindLocationModal";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect } from "react-redux";
import { ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showQuickOrder = "showQuickOrder",
    showShipTo = "showShipTo",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showQuickOrder]: boolean;
        [fields.showShipTo]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    const changeCustomerPageUrl = getPageLinkByPageType(state, "ChangeCustomerPage")?.url;
    const isAuthenticated = xIsAuthenticated(state);
    const signInUrl = getPageLinkByPageType(state, "SignInPage")?.url;

    const { fulfillmentAddressLabel, fulfillmentMethodLabel, fulfillmentMethod, pickUpWarehouse } =
        xGetFulfillmentLabel(state);

    let fulfillmentUrl: string | undefined;
    if (!isAuthenticated || !fulfillmentMethodLabel) {
        fulfillmentUrl = signInUrl;
    } else {
        fulfillmentUrl = changeCustomerPageUrl;
    }

    return {
        quickOrderPageUrl: getPageLinkByPageType(state, "QuickOrderPage")?.url,
        fulfillmentAddressLabel,
        fulfillmentMethodLabel,
        fulfillmentUrl,
        fulfillmentMethod,
        isTabletScreen: state.xCommon.isTabletScreen,
        pickUpWarehouse,
    };
};

const mapDispatchToProps = {
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse as any),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

interface HeaderActionsStyle {
    wrapper?: InjectableCss;
    quickOrder?: HeaderClickableStyle;
    customerSelector?: HeaderClickableStyle;
    customerSelectorPopup?: TippyPopupStyle;
    fulfillmentMethodLabel?: TypographyPresentationProps;
}

const styles: HeaderActionsStyle = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    quickOrder: {
        clickable: {
            css: css`
                margin-right: 24px;
                &:hover {
                    animation: animateColors;
                    animation-delay: 0s;
                    animation-duration: 2s;
                    background: ${color("common.ui2")};
                }
                @keyframes animateColors {
                    0% {
                        background: ${color("common.ui1")};
                    }
                    100% {
                        background: ${color("common.ui2")};
                    }
                }
            `,
        },
    },
    customerSelector: {
        clickable: {
            css: css`
                margin-right: 24px;
                ${xScreen("lte", "xs")} {
                    display: none;
                }
            `,
        },
    },
    customerSelectorPopup: {
        main: {
            css: css`
                &.tippy-box {
                    & > .tippy-content {
                        padding: 16px;
                    }
                }
            `,
        },
    },
};

const HeaderActions = ({
    fields: { showQuickOrder, showShipTo },
    quickOrderPageUrl,
    fulfillmentAddressLabel,
    fulfillmentMethodLabel,
    fulfillmentUrl,
    history,
    fulfillmentMethod,
    isTabletScreen,
    id,
    pickUpWarehouse,
    updatePickUpWarehouse,
}: Props) => {
    const [isPopupShown, setIsPopupShown] = useState(false);
    const [isFindLocationOpen, setIsFindLocationOpen] = useState(false);
    const { height: headerHeight } = useHeaderHeight();
    const toasterContext = React.useContext(ToasterContext);

    const handleQuickOrderClick = () => {
        if (!quickOrderPageUrl) {
            return;
        }
        history.push(quickOrderPageUrl);
    };

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };

    const handleChangeCustomerClick = () => {
        if (fulfillmentMethod === FulfillmentMethod.PickUp) {
            return;
        }
        if (!fulfillmentUrl) {
            return;
        }
        history.push(fulfillmentUrl);
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            {showQuickOrder && (
                <HeaderClickable
                    onClick={handleQuickOrderClick}
                    iconProps={{ src: QuickOrder }}
                    data-test-selector="header_quickorder"
                    aria-label="Quick Order"
                    extendedStyles={styles.quickOrder}
                >
                    {`${translate("Quick")}`}
                    <Typography as="div">{`${translate("Order")}`}</Typography>
                </HeaderClickable>
            )}
            {showShipTo && (
                <TippyPopup
                    extendedStyles={styles.customerSelectorPopup}
                    content={<MiniChangeShipTo onChangeShipToClick={handleChangeCustomerClick} />}
                    arrow={!isTabletScreen}
                    offset={[0, 26]}
                    maxWidth={isTabletScreen ? "none" : 350}
                    placement="bottom-end"
                    type="menu"
                    mobileMode={fulfillmentMethod === FulfillmentMethod.PickUp ? "left-side" : false}
                    closeButton={false}
                    onTriggerPopupOpen={isOpen => setIsPopupShown(isOpen)}
                    appendTo={isTabletScreen ? () => document.body : "parent"}
                    popperOptions={{
                        modifiers: [
                            {
                                name: "preventOverflow",
                                options: {
                                    padding: 0,
                                },
                            },
                            {
                                name: "computeStyles",
                                options: {
                                    roundOffsets: ({ x, y }: any) => ({
                                        x,
                                        y: isTabletScreen ? headerHeight : y,
                                    }),
                                },
                            },
                        ],
                    }}
                >
                    <>
                        <HeaderClickable
                            isPopupShown={isPopupShown}
                            onClick={handleChangeCustomerClick}
                            iconProps={{ src: Location }}
                            data-test-selector="header_changeShipTo"
                            aria-label="Change Customer"
                            titleProps={{
                                icon: {
                                    src: ChevronDown,
                                },
                            }}
                            extendedStyles={styles.customerSelector}
                        >
                            <>
                                <Typography {...styles.fulfillmentMethodLabel}>{`${
                                    fulfillmentMethodLabel || "Ship"
                                }`}</Typography>
                                <Typography ellipsis className="sub-title">{`${
                                    fulfillmentAddressLabel || ""
                                } `}</Typography>
                            </>
                        </HeaderClickable>
                        <FindLocationModal
                            modalIsOpen={isFindLocationOpen}
                            onWarehouseSelected={handlePickUpAddressChange}
                            onModalClose={() => setIsFindLocationOpen(false)}
                        />
                    </>
                </TippyPopup>
            )}
            <XngZone contentId={id} zoneName={SignInZoneName} hint="Common/HeaderSignIn" />
            <XngZone contentId={id} zoneName={CartLinkZoneName} hint="Common/CartLink" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<FieldDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderActions)),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.showQuickOrder,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.showShipTo,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
